import React from 'react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import { SectionHeading, SubHeading, Para, Mark, Code, Callout, Output, Pill, QA } from './articleKit';

type Author = { name: string; fullName: string; avatar?: string };

const TIMELINE = [
 { era: 'ES5', year: '2009', features: ['Strict mode', 'JSON support', 'Array methods', 'Object enhancements'] },
 { era: 'ES6', year: '2015', features: ['let / const', 'Arrow functions', 'Classes', 'Modules', 'Promises', 'Template literals', 'Destructuring', 'Spread & rest'] },
 { era: 'ES2016+', year: '2016 →', features: ['Async/await', 'Optional chaining', 'Nullish coalescing', 'BigInt', 'Private class fields', 'Top-level await'] },
];

export const JSES6Intro: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">Introduction to ES6+</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    The 2015 upgrade that turned JavaScript from a browser scripting language into a full application-development language — and what &quot;modern JavaScript&quot; really means.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="12 min read" />
  </div>

  {/* 1. Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>Before 2015, JavaScript was hugely popular but had real pain points that made large apps hard to build.</Para>

   <SubHeading>Variable management</SubHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `var name = "Sachin";\nif (true) {\n  var name = "Rahul"; // same variable — leaks out of the block\n}\nconsole.log(name); // "Rahul"` }]} defaultLanguage="javascript" id="es6-var" />

   <SubHeading>String concatenation</SubHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `var message = "Hello " + name + ", Welcome!"; // hard to read` }]} defaultLanguage="javascript" id="es6-concat" />

   <SubHeading>Callback hell</SubHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `getUser(function (user) {\n  getOrders(user.id, function (orders) {\n    getPayments(orders.id, function (payments) {\n      console.log(payments); // deep nesting, hard to maintain\n    });\n  });\n});` }]} defaultLanguage="javascript" id="es6-callback" />

   <SubHeading>No modules</SubHeading>
   <CodeBlock snippets={[{ language: 'html', code: `<script src="user.js"></script>\n<script src="order.js"></script>\n<script src="payment.js"></script>\n<!-- everything in the global scope -->` }]} defaultLanguage="html" id="es6-scripts" />
   <Para>JavaScript needed modernization — and ES6 delivered it.</Para>
  </section>

  {/* 2. Definition */}
  <section className="mb-12">
   <SectionHeading n={2}>Definition</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200"><Mark>ES6 (ECMAScript 2015)</Mark> was the largest upgrade ever made to JavaScript — adding modern syntax, better scoping, classes, modules, promises, arrow functions, template literals, destructuring, spread, and more.</p>
   </div>
   <Para>It transformed JavaScript from a browser scripting language into a modern application-development language.</Para>
  </section>

  {/* 3. ES vs JS */}
  <section className="mb-12">
   <SectionHeading n={3}>JavaScript vs ECMAScript</SectionHeading>
   <div className="my-2 grid sm:grid-cols-2 gap-3">
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
     <div className="text-sm font-bold text-gray-900 dark:text-white mb-1">ECMAScript</div>
     <div className="text-[13px] text-gray-600 dark:text-gray-400">The official spec — the &quot;rule book&quot; that defines how the language behaves.</div>
    </div>
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
     <div className="text-sm font-bold text-gray-900 dark:text-white mb-1">JavaScript</div>
     <div className="text-[13px] text-gray-600 dark:text-gray-400">The implementation developers actually write, following that spec.</div>
    </div>
   </div>
   <Para>Engines all implement the ECMAScript standard:</Para>
   <div className="flex flex-wrap gap-2">{['Chrome / Node.js → V8', 'Firefox → SpiderMonkey', 'Safari → JavaScriptCore'].map((e) => <Pill key={e}>{e}</Pill>)}</div>
  </section>

  {/* 4. Why revolutionary */}
  <section className="mb-12">
   <SectionHeading n={4}>Why ES6 Was Revolutionary</SectionHeading>
   <Para>Before ES6, JavaScript was seen as a small scripting language. After ES6, it became suitable for frontend, backend, mobile, desktop, cloud, and large enterprise systems — enabling modern frameworks:</Para>
   <div className="flex flex-wrap gap-2">{['React', 'Angular', 'Vue', 'Node.js', 'Next.js', 'NestJS'].map((e) => <Pill key={e}>{e}</Pill>)}</div>
  </section>

  {/* 5. Timeline */}
  <section className="mb-12">
   <SectionHeading n={5}>Timeline of Evolution</SectionHeading>
   <div className="my-6 space-y-3">
    {TIMELINE.map((t) => (
     <div key={t.era} className="flex gap-4">
      <div className="w-24 shrink-0 text-right">
       <div className="text-sm font-bold text-amber-600 dark:text-amber-400">{t.era}</div>
       <div className="text-[11px] text-gray-500 dark:text-gray-500">{t.year}</div>
      </div>
      <div className="flex-1 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3">
       <div className="flex flex-wrap gap-1.5">{t.features.map((f) => <Pill key={f}>{f}</Pill>)}</div>
      </div>
     </div>
    ))}
   </div>
   <Callout tone="info">&quot;Modern JavaScript&quot; usually means <Mark>ES6+</Mark> — ES6 and everything released after it.</Callout>
  </section>

  {/* 6. Core philosophy */}
  <section className="mb-12">
   <SectionHeading n={6}>Core Philosophy of ES6+</SectionHeading>
   <Para>ES6 was designed around a few clear goals. For example, cleaner syntax:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `// before\nvar message = "Hello " + name + "!";\n\n// after\nconst message = \`Hello \${name}!\`;` }]} defaultLanguage="javascript" id="es6-philosophy" />
   <div className="my-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
    {[
     ['Cleaner syntax', 'template literals, arrow functions'],
     ['Better scoping', 'let / const instead of var'],
     ['Better async', 'promises & async/await over callbacks'],
     ['Better organization', 'modules instead of global scripts'],
    ].map(([t, d]) => (
     <div key={t} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2">
      <div className="text-[13px] font-semibold text-gray-900 dark:text-white">{t}</div>
      <div className="text-[12px] text-gray-600 dark:text-gray-400">{d}</div>
     </div>
    ))}
   </div>
  </section>

  {/* 7. Most important features */}
  <section className="mb-12">
   <SectionHeading n={7}>Most Important ES6+ Features</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const name = "Sachin";              // let / const\nconst add = (a, b) => a + b;         // arrow functions\nconst msg = \`Hello \${name}\`;          // template literals\nconst { id } = user;                 // destructuring\nconst copy = [...items];             // spread\nclass User {}                        // classes\nimport { thing } from "./module.js"; // modules\nconst data = await fetchData();      // async/await` }]} defaultLanguage="javascript" id="es6-features" />
  </section>

  {/* 8. Real projects */}
  <section className="mb-12">
   <SectionHeading n={8}>ES6 in Real Projects</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `// before\nvar orders = [];\nfunction getOrders() {}\n\n// after — cleaner, safer, more maintainable\nconst orders = [];\nconst getOrders = async () => {};` }]} defaultLanguage="javascript" id="es6-projects" />
   <Para>This is why modern companies expect ES6+ proficiency.</Para>
  </section>

  {/* 9. TypeScript + Java */}
  <section className="mb-12">
   <SectionHeading n={9}>TypeScript &amp; Java Connections</SectionHeading>
   <Para>TypeScript builds directly on modern JavaScript — it&apos;s essentially <Mark>ES6+ plus a type system</Mark>. To be strong in TS, you must first know ES6+.</Para>
   <CodeBlock snippets={[{ language: 'typescript', code: `const user: string = "Sachin"; // ES6+ syntax + types` }]} defaultLanguage="typescript" id="es6-ts" />
   <Para>Many ES6 ideas mirror Java&apos;s evolution — lambdas/streams and classes:</Para>
   <CodeBlock snippets={[{ language: 'java', code: `list.stream().filter(x -> x > 10); // Java lambda\n// JS: list.filter(x => x > 10);` }]} defaultLanguage="java" id="es6-java" />
  </section>

  {/* 10. Misconceptions */}
  <section className="mb-12">
   <SectionHeading n={10}>Common Misconceptions</SectionHeading>
   <Callout tone="warning" title="ES6 and JavaScript are different things">ES6 is a <em>version</em> of the JavaScript (ECMAScript) specification, not a separate language.</Callout>
   <Callout tone="warning" title="Only frontend devs need ES6">Backend devs using Node.js, NestJS, and Express rely on ES6+ just as heavily.</Callout>
   <Callout tone="warning" title="ES6 only added arrow functions">It introduced dozens of major features — classes, modules, promises, destructuring, and more.</Callout>
  </section>

  {/* 11. Interview concepts */}
  <section className="mb-12">
   <SectionHeading n={11}>Interview Concepts</SectionHeading>
   <div className="flex flex-wrap gap-2">
    {['What is ECMAScript?', 'What is ES6?', 'Why was ES6 introduced?', 'JavaScript vs ECMAScript', 'Most important ES6 features', 'How did ES6 improve JS?'].map((q) => <Pill key={q}>{q}</Pill>)}
   </div>
  </section>

  {/* 12. Real interview questions */}
  <section className="mb-12">
   <SectionHeading n={12}>Real Interview Questions</SectionHeading>
   <QA q="What is the difference between JavaScript and ECMAScript?">
    ECMAScript is the language <em>specification</em>; JavaScript is the implementation engines run based on that spec.
   </QA>
   <QA q="Why use let/const instead of var?">
    <Code>let</Code>/<Code>const</Code> are block-scoped and safer, avoiding the leakage and hoisting surprises of function-scoped <Code>var</Code>.
   </QA>
   <QA q="What does ES6+ mean?">
    ES6 (2015) plus all later yearly editions (ES2016, ES2017, …) — collectively &quot;modern JavaScript.&quot;
   </QA>
   <QA q="Name the most impactful ES6 features.">
    let/const, arrow functions, template literals, destructuring, spread/rest, classes, modules, and promises.
   </QA>
  </section>

  {/* 13. Mental model */}
  <section className="mb-12">
   <SectionHeading n={13}>Mental Model</SectionHeading>
   <Para>JavaScript before ES6 was a <Mark>basic toolkit</Mark>. After ES6 it became a <Mark>professional engineering toolkit</Mark> — same language, far more capability.</Para>
  </section>

  {/* 14 & 15 */}
  <section className="mb-4">
   <SectionHeading n={14}>Interview One-Liner &amp; Takeaway</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <div className="text-xs font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-2">Interview One-Liner</div>
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     ES6 (ECMAScript 2015) was a major upgrade that introduced let/const, arrow functions, classes, modules, promises, destructuring, and template literals — making JavaScript scalable and maintainable.
    </p>
   </div>
   <Callout tone="info" title="Most important takeaway">
    Modern JavaScript means ES6+. Nearly every React, Node.js, Next.js, NestJS, and TypeScript app relies on it. Get comfortable with the ES6 fundamentals first — they&apos;re the foundation for everything that follows.
   </Callout>
  </section>
 </div>
);

export default JSES6Intro;
